/**
 * Extracted handler logic for ListDetailPage.
 * Contains all CRUD handlers, optimistic reorder, and drag-end callbacks.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import api from '@/lib/api';
import type { TodoItemResponse, TodoSectionResponse, TodoListDetailResponse } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';

interface UseListDetailHandlersOptions {
  listId: string;
  list: TodoListDetailResponse | null;
  createSection: (input: {
    name: string;
    description?: string;
    parentSectionId?: string;
    sectionType?: 'timeframe' | 'category';
  }) => Promise<TodoSectionResponse>;
  updateSection: (vars: {
    sectionId: string;
    input: {
      name?: string;
      description?: string;
      sortOrder?: number;
      sectionType?: 'timeframe' | 'category';
      parentSectionId?: string | null;
      hiddenFromPublic?: boolean;
    };
  }) => Promise<TodoSectionResponse>;
  deleteSection: (sectionId: string) => Promise<void>;
  createItem: (input: {
    title: string;
    description?: string;
    sectionId?: string;
  }) => Promise<TodoItemResponse>;
  updateItem: (vars: {
    itemId: string;
    input: {
      title?: string;
      description?: string;
      status?: 'pending' | 'in_progress' | 'completed';
      sectionId?: string | null;
      sortOrder?: number;
    };
  }) => Promise<TodoItemResponse>;
  deleteItem: (itemId: string) => Promise<void>;
  captureSnapshot: () => void;
  undo: () => void;
}

interface DialogActions {
  setCreateSectionOpen: (open: boolean) => void;
  setCreateTimeframeOpen: (open: boolean) => void;
  setCreateItemOpen: (open: boolean) => void;
  setEditSectionOpen: (open: boolean) => void;
  setEditingSection: (section: TodoSectionResponse | null) => void;
  setEditItemOpen: (open: boolean) => void;
  setEditingItem: (item: TodoItemResponse | null) => void;
}

export function useListDetailHandlers(
  options: UseListDetailHandlersOptions,
  dialogs: DialogActions,
) {
  const {
    listId,
    list,
    createSection,
    updateSection,
    deleteSection,
    createItem,
    updateItem,
    deleteItem,
    captureSnapshot,
    undo,
  } = options;

  const queryClient = useQueryClient();
  const detailKey = queryKeys.todoLists.detail(listId);
  const { toast } = useToast();

  // =========================================================================
  // Optimistic reorder
  // =========================================================================

  const optimisticSectionReorder = useCallback(
    async (reorderedIds: string[]) => {
      captureSnapshot();
      await queryClient.cancelQueries({ queryKey: detailKey });
      queryClient.setQueryData<TodoListDetailResponse>(detailKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          sections: old.sections.map((s) => {
            const idx = reorderedIds.indexOf(s.sectionId);
            return idx !== -1 ? { ...s, sortOrder: idx } : s;
          }),
        };
      });
      api.reorderTodoSections(listId, reorderedIds).finally(() => {
        queryClient.invalidateQueries({ queryKey: detailKey });
      });
    },
    [captureSnapshot, detailKey, listId, queryClient],
  );

  const optimisticItemReorder = useCallback(
    async (sectionId: string | null, reorderedIds: string[]) => {
      captureSnapshot();
      await queryClient.cancelQueries({ queryKey: detailKey });
      queryClient.setQueryData<TodoListDetailResponse>(detailKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item) => {
            const idx = reorderedIds.indexOf(item.itemId);
            return idx !== -1 ? { ...item, sortOrder: idx } : item;
          }),
        };
      });
      api.reorderTodoItems(listId, sectionId, reorderedIds).finally(() => {
        queryClient.invalidateQueries({ queryKey: detailKey });
      });
    },
    [captureSnapshot, detailKey, listId, queryClient],
  );

  // =========================================================================
  // Drag-end handlers
  // =========================================================================

  const createTopLevelDragEnd = useCallback(
    (topLevelSections: TodoSectionResponse[]) => (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = topLevelSections.findIndex((s) => s.sectionId === String(active.id));
      const newIndex = topLevelSections.findIndex((s) => s.sectionId === String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(topLevelSections, oldIndex, newIndex);
        optimisticSectionReorder(reordered.map((s) => s.sectionId));
      }
    },
    [optimisticSectionReorder],
  );

  const createUnsortedItemDragEnd = useCallback(
    (unsortedItems: TodoItemResponse[]) => (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = unsortedItems.findIndex((i) => i.itemId === String(active.id));
      const newIndex = unsortedItems.findIndex((i) => i.itemId === String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(unsortedItems, oldIndex, newIndex);
        optimisticItemReorder(
          null,
          reordered.map((i) => i.itemId),
        );
      }
    },
    [optimisticItemReorder],
  );

  // =========================================================================
  // Item handlers
  // =========================================================================

  const handleToggleItem = useCallback(
    async (item: TodoItemResponse) => {
      captureSnapshot();
      const newStatus = item.status === 'completed' ? 'pending' : 'completed';
      await updateItem({ itemId: item.itemId, input: { status: newStatus } });
    },
    [captureSnapshot, updateItem],
  );

  const handleDeleteItem = useCallback(
    async (itemId: string) => {
      captureSnapshot();
      await deleteItem(itemId);
      toast({
        title: 'Item deleted',
        action: (
          <ToastAction altText="Undo" onClick={undo}>
            Undo
          </ToastAction>
        ),
      });
    },
    [captureSnapshot, deleteItem, toast, undo],
  );

  const handleEditItem = useCallback(
    (item: TodoItemResponse) => {
      dialogs.setEditingItem(item);
      dialogs.setEditItemOpen(true);
    },
    [dialogs],
  );

  const handleSaveItem = useCallback(
    async (
      itemId: string,
      input: { title?: string; description?: string; sectionId?: string | null },
    ) => {
      captureSnapshot();
      await updateItem({ itemId, input });
      dialogs.setEditItemOpen(false);
      dialogs.setEditingItem(null);
    },
    [captureSnapshot, updateItem, dialogs],
  );

  const handleUpdateItemTitle = useCallback(
    (itemId: string, title: string) => {
      captureSnapshot();
      updateItem({ itemId, input: { title } });
    },
    [captureSnapshot, updateItem],
  );

  const handleCreateItem = useCallback(
    async (input: { title: string; description?: string; sectionId?: string }) => {
      captureSnapshot();
      await createItem(input);
      dialogs.setCreateItemOpen(false);
    },
    [captureSnapshot, createItem, dialogs],
  );

  const handleCreateItemBelow = useCallback(
    async (sectionId: string | null, afterItemId: string, title: string) => {
      captureSnapshot();
      const newItem = await createItem({ title, sectionId: sectionId ?? undefined });
      if (list) {
        const sectionItems = list.items
          .filter((i) => i.sectionId === sectionId && i.status !== 'completed')
          .sort((a, b) => a.sortOrder - b.sortOrder);
        const ids = sectionItems.map((i) => i.itemId);
        const afterIndex = ids.indexOf(afterItemId);
        ids.splice(afterIndex + 1, 0, newItem.itemId);
        await api.reorderTodoItems(listId, sectionId, ids);
        queryClient.invalidateQueries({ queryKey: detailKey });
      }
    },
    [captureSnapshot, createItem, list, listId, queryClient, detailKey],
  );

  // =========================================================================
  // Section handlers
  // =========================================================================

  const handleEditSection = useCallback(
    (section: TodoSectionResponse) => {
      dialogs.setEditingSection(section);
      dialogs.setEditSectionOpen(true);
    },
    [dialogs],
  );

  const handleSaveSection = useCallback(
    async (
      sectionId: string,
      input: { name?: string; description?: string; parentSectionId?: string | null },
    ) => {
      captureSnapshot();
      await updateSection({ sectionId, input });
      dialogs.setEditSectionOpen(false);
      dialogs.setEditingSection(null);
    },
    [captureSnapshot, updateSection, dialogs],
  );

  const handleDeleteSection = useCallback(
    (sectionId: string) => {
      captureSnapshot();
      deleteSection(sectionId);
    },
    [captureSnapshot, deleteSection],
  );

  const handleCreateSection = useCallback(
    async (input: {
      name: string;
      description?: string;
      parentSectionId?: string;
      sectionType: 'category';
    }) => {
      captureSnapshot();
      await createSection(input);
      dialogs.setCreateSectionOpen(false);
    },
    [captureSnapshot, createSection, dialogs],
  );

  const handleCreateTimeframe = useCallback(
    async (input: { name: string; description?: string; sectionType: 'timeframe' }) => {
      captureSnapshot();
      await createSection(input);
      dialogs.setCreateTimeframeOpen(false);
    },
    [captureSnapshot, createSection, dialogs],
  );

  const handleToggleHiddenFromPublic = useCallback(
    (sectionId: string, hidden: boolean) => {
      captureSnapshot();
      updateSection({ sectionId, input: { hiddenFromPublic: hidden } });
    },
    [captureSnapshot, updateSection],
  );

  // =========================================================================
  // Settings handler
  // =========================================================================

  const handleSettingsSave = useCallback(
    async (input: { isPublic?: boolean; publicSlug?: string }) => {
      await api.updateTodoList(listId, input);
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
    [listId, queryClient, detailKey],
  );

  const handleDeleteList = useCallback(async () => {
    await api.deleteTodoList(listId);
    queryClient.removeQueries({ queryKey: detailKey });
    await queryClient.invalidateQueries({ queryKey: queryKeys.todoLists.my() });
  }, [listId, queryClient, detailKey]);

  return {
    optimisticSectionReorder,
    optimisticItemReorder,
    createTopLevelDragEnd,
    createUnsortedItemDragEnd,
    handleToggleItem,
    handleDeleteItem,
    handleEditItem,
    handleSaveItem,
    handleUpdateItemTitle,
    handleCreateItem,
    handleCreateItemBelow,
    handleEditSection,
    handleSaveSection,
    handleDeleteSection,
    handleCreateSection,
    handleCreateTimeframe,
    handleToggleHiddenFromPublic,
    handleSettingsSave,
    handleDeleteList,
  };
}
